import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import toNumber from 'lodash-es/toNumber';
import isNull from 'lodash-es/isNull.js';
import isUndefined from 'lodash-es/isUndefined';
import map from 'lodash-es/map';

/***
 * Класс для range и работы с его инпутмасками
 * @example
 *
 * Методы:
 * update({ max, min, float, mask }) - обновление параметров блока. Подробнее в описании
 * updateValues({ valueMin, valueMax }) - Обновление значений инпутов и выбранного диапазона
 *
 * Аттрибуты:
 * data-max - {number|null, default = Infinity} - максимальное число. Если null то Infinity
 * data-min - {number|null, default = -Infinity} - минимальное число. Если null то -Infinity
 * data-float - {number|null, default = 0} - цифр после запятой. Если null то 0
 * data-mask - {string|null, default = null} - инпутмаска, которая будет передана инпутам изначально. Если ее нет, то она добавится в js
 */
class FilterRange {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом FilterRange
   * @param force {Boolean} принудительно инициализирование
   */
  constructor(elem, force) {
    this.BlockError = new BlockError({ blockName: 'FilterRange', destroy: this.destroy });

    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.FilterRange = this;
    this.block.AsyncFilterRange = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'filter-range_inited' });
    this.LazyInit.init();
    if (force) this.LazyInit.earlyInit();
  }

  init = async () => {
    await window.Loading.Inputmask.catch((err) => {
      throw new Error(err);
    });

    this.setOptions();
    this.updateMask();

    this.inputMin.addEventListener('change', this.updateInputMax);
    this.inputMax.addEventListener('change', this.updateInputMin);
    if (this.list) {
      this.list.addEventListener('change', this.onChangeRadio);
      this.updateDiapason();
    }

    this.promiseResolve(this);
    this.block.AsyncFilterRange = this;
  };

  setOptions = () => {
    this.mask = this.block.dataset['mask'] || '"alias": "numeric", "allowMinus": false, "_radixDance": false, "rightAlign": false, "showMaskOnHover": false, "showMaskOnFocus": false';

    this.min = toNumber(this.block.dataset['min']);
    this.min = isNull(this.min) || isNaN(this.min) ? -Infinity : this.min;

    this.max = toNumber(this.block.dataset['max']);
    this.max = isNull(this.max) || isNaN(this.max) ? Infinity : this.max;

    this.float = toNumber(this.block.dataset['float']);
    this.float = isNull(this.float) || isNaN(this.float) ? 0 : this.float;

    this.inputMin = this.block.querySelector('[data-input-min]');
    if (!this.inputMin) return this.BlockError.error('Отсутствует [data-input-min]');

    this.inputMax = this.block.querySelector('[data-input-max]');
    if (!this.inputMax) return this.BlockError.error('Отсутствует [data-input-max]');

    this.list = this.block.querySelector('[data-list-radio]');
    this.diaposons = this.getDiapasons();
  };

  /**
   * Обработка изменения выбранной радиоконопки
   * @param event {InputEvent} - событие
   */
  onChangeRadio = (event) => {
    const values = event.target.getAttribute('value').split('-');
		this.updateMask();
    this.inputMin.value = values[0];
    this.inputMax.value = values[1];
		this.inputMin.dispatchEvent(new CustomEvent('change', {bubbles: true, detail: {isChangeRadio: true}}));
		this.inputMax.dispatchEvent(new CustomEvent('change', {bubbles: true, detail: {isChangeRadio: true}}));
	};

  /** Обновление маски */
  updateMask = () => {
    const newMask = this.getMask();
    this.updateMaskOfInput(this.inputMin, newMask);
    this.updateMaskOfInput(this.inputMax, newMask);
  };

  /**
   * Обновление маски максимума
   * @param event {InputEvent | CustomEvent}
   */
  updateInputMax = (event) => {
    if (this.inputMin.inputmask) this.inputMin.inputmask.setValue(this.inputMin.value);
    const val = this.inputMax.value;
    const newMask = this.getMask({ min: toNumber(this.inputMin.value || this.min) });
    this.updateMaskOfInput(this.inputMax, newMask);
    this.inputMax.value = val; // маска очищает крайнее значение
		if (event && event.detail && event.detail.isChangeRadio) return;
		if (this.list) this.updateDiapason();
  };

  /**
   * Обновление маски минимума
   * @param event {InputEvent | CustomEvent}
   */
  updateInputMin = (event) => {
    if (this.inputMax.inputmask) this.inputMax.inputmask.setValue(this.inputMax.value);
    const val = this.inputMin.value;
    const newMask = this.getMask({ max: toNumber(this.inputMax.value || this.max) });
    this.updateMaskOfInput(this.inputMin, newMask);
    this.inputMin.value = val; // маска очищает крайнее значение
    if (event && event.detail && event.detail.isChangeRadio) return;
    if (this.list) this.updateDiapason();
  };

  /** Обновление выбранного диапазона на основе ввода */
  updateDiapason = () => {
    const valueMin = toNumber(this.inputMin.value || this.min);
    const valueMax = toNumber(this.inputMax.value || this.max);
    for (const { min, max, elem } of this.diaposons) {
      const inDiapason = !elem.disabled && this.inDiapason({ valueMin, valueMax, min, max });
      elem.checked = inDiapason;
      if (inDiapason) break;
    }
  };

  /** Для бека
   * Обновление значений инпутов и выбранного диапазона
   * @param valueMin {String|undefined} - значение минимума
   * @param valueMax {String|undefined} - значение максимума
   */
  updateValues = ({ valueMin, valueMax } = {}) => {
    this.inputMin.value = valueMin ?? this.inputMin.value;
    this.inputMax.value = valueMax ?? this.inputMax.value;
    this.updateMask();
    this.updateDiapason();
  };

  /***
   * Обновляет параметры каунтера и маски валидации. Вызывает событие change
   * @param max {number|undefined} - максимальное значение
   * @param min {number|undefined} - минимальное значение
   * @param float {number|undefined} - цифр после запятой
   * @param mask {string} - новый начальный шаблон маски, которому будут добавляться параметры
   */
  update = ({ max, min, float, mask }) => {
    this.max =  isUndefined(max) ? this.max : toNumber(max);
    this.min =  isUndefined(min) ? this.min : toNumber(min);
    this.float = isUndefined(float) ? this.float : toNumber(float);
    this.mask = mask ? mask : this.mask;

    this.updateMask();
  };

  /**
   * Относится ли значения к диапазону
   * @param valueMin {Number} - значение минимума
   * @param valueMax {Number} - значение максимума
   * @param min {Number} - минимальное значение диапазона
   * @param max {Number} - максимальное значение диапазона
   * @return {boolean} относиться ли диапазон к переданному диапазону
   */
  inDiapason = ({ valueMin, valueMax, min, max }) => {
    return valueMin === min && valueMax === max;
  };

  /**
   * Получение маски для инпута
   * @param min {Number} - минимальная граница
   * @param max {Number} - максимальная граница
   * @param float {Number} - минимальная граница
   * @return {String} - строка с новой маской
   */
  getMask = ({ min = this.min, max = this.max, float = this.float } = {}) => {
    return this.mask + `${max === Infinity ? '' : `, "max": ${max}`} ${min === -Infinity ? '' : `, "min": ${min}`}, "digits": ${float}`;
  };

  /**
   * Получение данных о диапазонах
   * @return {{elem: HTMLInputElement, min: Number, max: Number}[]}
   */
  getDiapasons = () => {
    return map(this.list?.querySelectorAll('input[type="radio"]'), (elem) => {
      const [min, max] = elem.getAttribute('value').split('-');
      return { min: toNumber(min), max: toNumber(max), elem };
    });
  };

  /**
   * Обновление маски инпута
   * @param input {HTMLInputElement} - инпут для смены маски
   * @param mask {String} - маска
   */
  updateMaskOfInput = (input, mask) => {
    input.setAttribute('data-inputmask', mask);
    window.Block.Inputmask({
      postValidation: () => true,
      onBeforeWrite: (value) => value,
      onBeforeMask: (value, opts) => {
        if (value === '') return value;
        let num = toNumber(value);
        if (num < opts.min)
          return opts.min;
        else if (num > opts.max)
          return opts.max;
        return num;
      },
    }).mask(input);
  };

  destroy = () => {
    if (this.LazyInit) this.LazyInit.destroy();
    if (this.inputMin) this.inputMin.removeEventListener('change', this.updateInputMax);
    if (this.inputMax) this.inputMax.removeEventListener('change', this.updateInputMin);
    if (this.list) this.list.removeEventListener('change', this.onChangeRadio);
    if (this.block) {
      this.block.classList.remove('filter-range_inited');
      delete this.block.FilterRange;
      delete this.block.AsyncFilterRange;
    }
  };

  static init(container = document, force = false) {
    Array.from(container.querySelectorAll('.filter-range:not(.filter-range_inited)')).forEach((elem) => new FilterRange(elem, force));
  }
}

document.addEventListener('DOMContentLoaded', () => FilterRange.init());

// Экспорт;
window.Block.FilterRange = FilterRange;
